import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  MessageSquare, Wallet, AlertTriangle, CheckCircle2, XCircle,
  RefreshCw, TrendingUp, BarChart3, Bell, ExternalLink,
} from "lucide-react";
import { API } from "../../config";

const TYPE_LABEL = {
  welcome: "Welcome",
  otp: "OTP Firma",
  other: "Altro",
};

const stateColor = (state) => {
  if (state === "critical") return { text: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", dot: "bg-red-500" };
  if (state === "warning") return { text: "text-lojack-clause-400", bg: "bg-lojack-clause-500/10", border: "border-lojack-clause-500/30", dot: "bg-lojack-clause-500" };
  return { text: "text-lojack-blue-400", bg: "bg-lojack-blue-500/10", border: "border-lojack-blue-500/30", dot: "bg-lojack-blue-500" };
};

const fmtEur = (v) => {
  if (v === null || v === undefined) return "—";
  return `€ ${Number(v).toFixed(2).replace(".", ",")}`;
};

export const SmsHealthWidget = ({ variant = "admin" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingAlert, setTestingAlert] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/dashboard/sms-health?days=14`);
      setData(r.data);
    } catch (e) {
      setData({ gateway_configured: false, error: "Errore caricamento" });
    } finally {
      setLoading(false);
    }
  };

  const triggerAlertCheck = async () => {
    setTestingAlert(true);
    try {
      await axios.post(`${API}/dashboard/sms-credit-alert/check`);
      await fetchData();
    } finally {
      setTestingAlert(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 60_000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        {loading ? <div className="spinner mx-auto" /> : <p className="text-gray-500 text-sm">Nessun dato</p>}
      </div>
    );
  }

  if (data.gateway_configured === false) {
    return (
      <div className="bg-[#111] border border-lojack-clause-600/30 rounded-2xl p-6" data-testid="sms-health-widget-disabled">
        <div className="flex items-center gap-3">
          <AlertTriangle className="text-lojack-clause-400" size={20} />
          <p className="text-white font-semibold">Gateway SMS non configurato</p>
        </div>
      </div>
    );
  }

  const c = stateColor(data.alert_state);
  const credit = data.credit || {};
  const totals = data.totals || {};
  const status24 = data.status_24h || {};
  const status30 = data.status_30d || {};
  const timeseries = data.timeseries || [];
  const maxTs = Math.max(1, ...timeseries.map(d => (d.sent || 0) + (d.failed || 0)));
  const priv = Boolean(data.gateway_private);

  return (
    <div className="bg-gradient-to-br from-[#0f1a1f] via-[#0a0f12] to-[#0f1a1f] border border-blue-500/20 rounded-2xl p-6 space-y-5" data-testid="sms-health-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <MessageSquare size={20} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">SMS Health</h3>
            <p className="text-gray-500 text-xs">Gateway SMS · monitoraggio in tempo reale</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!priv && <a
            href="https://sms.telcavoip.net"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-lojack-clause-500/15 border border-lojack-clause-500/30 text-lojack-clause-300 hover:bg-lojack-clause-500/25 transition-colors text-xs font-medium"
            data-testid="sms-recharge-link"
            title="Apri il portale gateway in una nuova scheda per ricaricare il credito"
          >
            <Wallet size={12} />
            Ricarica credito
            <ExternalLink size={10} />
          </a>}
          {variant === "it" && !priv && (
            <button
              onClick={triggerAlertCheck}
              disabled={testingAlert}
              className="p-2 text-gray-500 hover:text-lojack-clause-400 transition-colors"
              title="Forza verifica credito + alert (se applicabile)"
              data-testid="sms-health-trigger-alert"
            >
              <Bell size={16} className={testingAlert ? "animate-pulse" : ""} />
            </button>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-blue-400 transition-colors"
            data-testid="sms-health-refresh"
            title="Aggiorna"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Credit + Volumes row */}
      <div className={`grid grid-cols-1 ${priv ? "md:grid-cols-2" : "md:grid-cols-4"} gap-4`}>
        {/* Credit card (TelcaVoIP super_admin only) */}
        {!priv && <div className={`md:col-span-2 ${c.bg} ${c.border} border rounded-xl p-4 relative overflow-hidden`} data-testid="sms-credit-card">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wallet size={16} className={c.text} />
              <p className="text-gray-400 text-xs uppercase tracking-wider">Credito Residuo</p>
            </div>
            <span className={`${c.dot} w-2 h-2 rounded-full mt-1 animate-pulse`} />
          </div>
          <div className="flex items-baseline gap-3">
            <p className={`text-4xl font-bold ${c.text}`}>{fmtEur(credit.credit_eur)}</p>
            <p className="text-gray-500 text-sm">/ soglia {fmtEur(data.alert_threshold_eur)}</p>
          </div>
          <p className="text-gray-400 text-xs mt-2">
            ≈ {credit.estimated_sms_remaining ?? "—"} SMS rimanenti
          </p>
          {data.alert_state !== "ok" && (
            <div className="mt-3 space-y-2">
              <p className={`text-xs ${c.text} font-medium flex items-center gap-1.5`}>
                <AlertTriangle size={12} />
                {data.alert_state === "critical" ? "Credito critico — ricarica urgente" : "Credito sotto soglia di guardia"}
              </p>
              <a
                href="https://sms.telcavoip.net"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${data.alert_state === "critical" ? "bg-red-500 hover:bg-red-400 text-white" : "bg-lojack-clause-500 hover:bg-lojack-clause-400 text-black"}`}
                data-testid="sms-recharge-cta-inline"
              >
                <Wallet size={12} />
                Ricarica ora
                <ExternalLink size={10} />
              </a>
            </div>
          )}
          {data.last_alert_sent_at && (
            <p className="text-gray-600 text-[10px] mt-2">
              Ultimo alert email: {new Date(data.last_alert_sent_at).toLocaleString("it-IT")}
            </p>
          )}
        </div>}

        {/* Volumes 24h */}
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sms-volume-24h">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Inviati 24h</p>
          <p className="text-3xl font-bold text-white">{totals.last_24h ?? 0}</p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-lojack-blue-400 flex items-center gap-1"><CheckCircle2 size={11} />{status24.sent ?? 0}</span>
            <span className="text-red-400 flex items-center gap-1"><XCircle size={11} />{status24.failed ?? 0}</span>
          </div>
          <p className="text-gray-500 text-[10px] mt-1.5">Delivery rate: {data.delivery_rate_24h}%</p>
        </div>

        {/* Volumes 30d */}
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sms-volume-30d">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Inviati 30g</p>
          <p className="text-3xl font-bold text-white">{totals.last_30d ?? 0}</p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-lojack-blue-400 flex items-center gap-1"><CheckCircle2 size={11} />{status30.sent ?? 0}</span>
            <span className="text-red-400 flex items-center gap-1"><XCircle size={11} />{status30.failed ?? 0}</span>
          </div>
          <p className="text-gray-500 text-[10px] mt-1.5">Delivery rate: {data.delivery_rate_30d}%</p>
        </div>
      </div>

      {/* Timeseries chart */}
      <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sms-timeseries">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={14} className="text-blue-400" />
          <p className="text-white text-sm font-semibold">Andamento ultimi 14 giorni</p>
        </div>
        <div className="flex items-end gap-1 h-24">
          {timeseries.map((d, i) => {
            const total = (d.sent || 0) + (d.failed || 0);
            const heightPct = (total / maxTs) * 100;
            const sentPct = total > 0 ? (d.sent / total) * 100 : 0;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div className="w-full bg-gray-800/50 rounded-t overflow-hidden flex flex-col justify-end" style={{ height: `${heightPct || 2}%`, minHeight: "2px" }}>
                  {total > 0 && (
                    <>
                      <div className="bg-red-500/70" style={{ height: `${100 - sentPct}%` }} />
                      <div className="bg-blue-500/70" style={{ height: `${sentPct}%` }} />
                    </>
                  )}
                </div>
                <p className="text-gray-600 text-[9px] rotate-0">{d.date}</p>
                {total > 0 && (
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-black border border-gray-700 rounded px-2 py-1 text-[10px] whitespace-nowrap z-10">
                    <span className="text-lojack-blue-400">{d.sent} sent</span> · <span className="text-red-400">{d.failed} failed</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* By type + cost */}
      <div className={`grid grid-cols-1 ${priv ? "" : "md:grid-cols-2"} gap-4`}>
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sms-by-type">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Per tipo (14g)</p>
          {data.by_type.length === 0 ? (
            <p className="text-gray-600 text-xs">Nessun invio nel periodo</p>
          ) : (
            <div className="space-y-2">
              {data.by_type.map((row) => (
                <div key={row.type} className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">{TYPE_LABEL[row.type] || row.type}</span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-lojack-blue-400">{row.sent} ok</span>
                    {row.failed > 0 && <span className="text-red-400">{row.failed} ko</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!priv && <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sms-cost-month">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-purple-400" />
            <p className="text-gray-400 text-xs uppercase tracking-wider">Spesa mese corrente</p>
          </div>
          <p className="text-3xl font-bold text-purple-300">{fmtEur(data.current_month_cost_eur)}</p>
          <p className="text-gray-500 text-xs mt-1">{data.current_month_count} SMS spediti</p>
        </div>}
      </div>

      {/* Top failures (admin only) */}
      {variant === "admin" && data.top_failures && data.top_failures.length > 0 && (
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sms-top-failures">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
            <XCircle size={12} className="text-red-400" />Ultimi falliti (7g)
          </p>
          <div className="space-y-1.5">
            {data.top_failures.slice(0, 5).map((f, i) => (
              <div key={i} className="flex items-center justify-between text-xs gap-3">
                <span className="text-gray-400 font-mono">{f.phone_masked}</span>
                <span className="text-gray-500 truncate flex-1">{(f.error || "").slice(0, 60)}</span>
                <span className="text-gray-600 text-[10px] whitespace-nowrap">
                  {new Date(f.created_at).toLocaleDateString("it-IT")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-gray-600 text-[10px] text-center">
        Aggiornato: {new Date(data.generated_at).toLocaleString("it-IT")} · Auto-refresh 60s
      </p>
    </div>
  );
};

export default SmsHealthWidget;

SmsHealthWidget.propTypes = {
  variant: PropTypes.oneOf(["admin", "it"]),    // defaults to "admin" in destructure
};
